import { useCallback, useSyncExternalStore } from 'react';
import { useMediaQuery } from './useMediaQuery';

export type ThemePreference = 'light' | 'dark' | 'system';
export type Density = 'comfortable' | 'compact';

export const THEME_KEY = 'ols-mail.theme';
export const DENSITY_KEY = 'ols-mail.density';

const THEMES = ['light', 'dark', 'system'] as const;
const DENSITIES = ['comfortable', 'compact'] as const;

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return allowed.includes(value as T) ? (value as T) : fallback;
  } catch {
    return fallback;
  }
}

interface Appearance {
  theme: ThemePreference;
  density: Density;
}

/**
 * One appearance for the whole document (#343).
 *
 * This used to be `useState` inside the hook, so every component that called
 * `useTheme()` had its own copy: the top-bar toggle and Settings → General
 * each held a theme, and changing one left the other showing a stale value
 * (and writing it back on its next change). It is a module-level store now,
 * read through `useSyncExternalStore`, so every caller sees the same value.
 *
 * It also follows `storage` events, so a change made in the main window
 * reaches an open pop-out (message or compose window), and one made in a
 * pop-out reaches the main window.
 */
let current: Appearance = {
  theme: read(THEME_KEY, THEMES, 'system'),
  density: read(DENSITY_KEY, DENSITIES, 'comfortable'),
};
const listeners = new Set<() => void>();

/**
 * 'system' writes NO attribute, leaving prefers-color-scheme in charge; an
 * explicit choice stamps data-theme on <html>, which tokens.css honours in both
 * directions. The same values are applied pre-paint by public/theme-init.js,
 * loaded from index.html in every window (pop-outs included), so nothing
 * flashes the wrong palette or density.
 */
function applyToDocument({ theme, density }: Appearance) {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
  if (density === 'comfortable') root.removeAttribute('data-density');
  else root.setAttribute('data-density', density);
}

function persist({ theme, density }: Appearance) {
  try {
    if (theme === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, theme);
    localStorage.setItem(DENSITY_KEY, density);
  } catch {
    /* storage blocked — the preference just won't persist */
  }
}

function update(next: Appearance) {
  if (next.theme === current.theme && next.density === current.density) return;
  current = next;
  applyToDocument(current);
  persist(current);
  listeners.forEach((listener) => listener());
}

/** Re-reads storage: another window changed it (or a test reset it). */
export function reloadAppearance() {
  const next: Appearance = {
    theme: read(THEME_KEY, THEMES, 'system'),
    density: read(DENSITY_KEY, DENSITIES, 'comfortable'),
  };
  current = next;
  applyToDocument(current);
  listeners.forEach((listener) => listener());
}

if (typeof window !== 'undefined') {
  applyToDocument(current);
  window.addEventListener('storage', (event) => {
    if (event.key === null || event.key === THEME_KEY || event.key === DENSITY_KEY) {
      reloadAppearance();
    }
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): Appearance {
  return current;
}

/** The order the top-bar button steps through: every choice General offers. */
export function nextTheme(theme: ThemePreference): ThemePreference {
  return theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
}

export const THEME_LABELS: Record<ThemePreference, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

/**
 * Whether the app is painting dark right now: an explicit dark choice, or
 * `system` on a device that prefers dark. What the message frame's appearance
 * follows (ADR 0081), because the frame cannot read the app's tokens.
 */
export function useDarkTheme(): boolean {
  const { theme } = useTheme();
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)');
  return theme === 'dark' || (theme === 'system' && systemDark);
}

export function useTheme() {
  const appearance = useSyncExternalStore(subscribe, snapshot, snapshot);

  const setTheme = useCallback(
    (theme: ThemePreference) => update({ ...snapshot(), theme }),
    [],
  );
  const setDensity = useCallback(
    (density: Density) => update({ ...snapshot(), density }),
    [],
  );
  const toggleTheme = useCallback(() => {
    const now = snapshot();
    update({ ...now, theme: nextTheme(now.theme) });
  }, []);

  return {
    theme: appearance.theme,
    setTheme,
    toggleTheme,
    density: appearance.density,
    setDensity,
  };
}
